// src/pages/healthcare/consultation/consultationComponents/ConsultContentTabs.tsx
import React, { useState } from "react";
import { Tabs } from "antd";
import { BookOutlined, ApartmentOutlined, FileTextOutlined, IdcardOutlined, CreditCardOutlined, FolderOutlined, FormOutlined, BellOutlined, CalendarOutlined, HistoryOutlined, MedicineBoxOutlined, ExperimentOutlined, ThunderboltOutlined } from "@ant-design/icons";

import { Patient } from "../../../../types/healthcare/PatientTypes";
import { Appointment, CompletedAppointment } from "../../../../types/appointments/AppointmentTypes";
import { PatientTreatmentPlan } from "../../../../types/healthcare/TreatmentPlanTypes";
import { Company } from "../../../../types/admin/CompanyTypes";

import ConsultGuidancePanel from "./ConsultGuidancePanel";
import ConsultParticulars from "./ConsultParticularsTab";
import ProfileTab from "../../patient/modalComponents/ProfileTab";
import BillingTab from "../../patient/modalComponents/BillingTab";
import FilesTab from "../../patient/modalComponents/FilesTab";
import FormsTab from "../../patient/modalComponents/FormsTab";
import NotificationsTab from "../../patient/modalComponents/NotificationsTab";
import AppointmentTab from "../../patient/modalComponents/AppointmentTab";
import MedicalHistoryTab from "../../patient/modalComponents/MedicalHistoryTab";
import MedicationsTab from "../../patient/modalComponents/MedicationsTab";
import TestingTab from "../../patient/modalComponents/TestingTab";
import SupplementsTab from "../../patient/modalComponents/SupplementsTab";
import PlanDetailDrawer from "../../treatmentPlans/planComponents/PlanDetailDrawer";

const { TabPane } = Tabs;

const consultTabsStyle = `
.consult-content-tabs .ant-tabs-content {
	height: 100%;
	display: flex;
	flex-direction: column;
}
.consult-content-tabs .ant-tabs-content-holder {
	flex: 1;
	overflow: hidden;
}
.consult-content-tabs .ant-tabs-tabpane {
	height: 100%;
}
`;

interface ConsultContentTabsProps {
	appointment: Appointment;
	patient: Patient;
	setPatient: React.Dispatch<React.SetStateAction<Patient>>;
	consultation: CompletedAppointment;
	onVitalsChange: (data: string) => void;
	readOnly: boolean;
	consultSessionId: number | null;
	activePlan: PatientTreatmentPlan | null;
	onActivePlanClose: () => void;
	onPlanChange: () => void;
	company: Company;
	handleAutoSave: () => void;
}

const ConsultContentTabs: React.FC<ConsultContentTabsProps> = ({ appointment, patient, setPatient, consultation, onVitalsChange, readOnly, consultSessionId, activePlan, onActivePlanClose, onPlanChange, company, handleAutoSave }) => {
	// FilesTab requires a setter; VideoConsultation never read this state either.
	const [errorMessages, setErrorMessages] = useState<any[]>([]);

	return (
		<>
			<style>{consultTabsStyle}</style>
			<Tabs defaultActiveKey={"guidance"} size="small" style={{ height: "100%", display: "flex", flexDirection: "column" }} tabBarStyle={{ margin: 0, padding: "4px 12px", minHeight: 32, flexShrink: 0 }} className="consult-content-tabs">
				{/* Guidance */}
				<TabPane
					tab={
						<span style={{ fontSize: 12 }}>
							<BookOutlined /> Guidance
						</span>
					}
					key="guidance"
					style={{ height: "100%", overflow: "hidden" }}
				>
					<div style={{ height: "100%", overflow: "hidden", padding: 8 }}>
						<ConsultGuidancePanel sessionId={consultSessionId} appointmentId={appointment.id} isDisabled={readOnly} />
					</div>
				</TabPane>

				{/* Treatment Plans */}
				<TabPane
					tab={
						<span style={{ fontSize: 12 }}>
							<ApartmentOutlined /> Treatment Plans
						</span>
					}
					key="treatment-plans"
					style={{ height: "100%", overflow: "hidden" }}
				>
					<div style={{ height: "100%", overflow: "auto", padding: 8 }}>
						<PlanDetailDrawer plan={activePlan} isConsult showToolbar onClose={onActivePlanClose} onPlanChange={onPlanChange} />
					</div>
				</TabPane>

				{/* Consult particulars */}
				<TabPane
					tab={
						<span style={{ fontSize: 12 }}>
							<FileTextOutlined /> Particulars
						</span>
					}
					key="particulars"
					style={{ height: "100%", overflow: "hidden" }}
				>
					<div style={{ height: "100%", overflow: "auto", padding: 8 }}>
						<ConsultParticulars consult={consultation} patient={patient} setPatient={setPatient} readOnly={readOnly} onDataChange={onVitalsChange} />
					</div>
				</TabPane>

				{/* Patient Profile */}
				<TabPane
					tab={
						<span style={{ fontSize: 12 }}>
							<IdcardOutlined /> Personal
						</span>
					}
					key="personal"
					style={{ height: "100%", overflow: "hidden" }}
				>
					<div style={{ height: "100%", overflow: "auto", padding: 8 }}>
						<ProfileTab updatedPatient={patient} setUpdatedPatient={setPatient} handleAutoSave={handleAutoSave} isAnon={patient.is_anonymous as boolean} />
					</div>
				</TabPane>

				{/* Billing */}
				<TabPane
					tab={
						<span style={{ fontSize: 12 }}>
							<CreditCardOutlined /> Billing
						</span>
					}
					key="billing"
					style={{ height: "100%", overflow: "hidden" }}
				>
					<div style={{ height: "100%", overflow: "auto", padding: 8 }}>
						<BillingTab updatedPatient={patient} setUpdatedPatient={setPatient} handleAutoSave={handleAutoSave} />
					</div>
				</TabPane>

				{/* Resources */}
				<TabPane
					tab={
						<span style={{ fontSize: 12 }}>
							<FolderOutlined /> Resources
						</span>
					}
					key="files"
					style={{ height: "100%", overflow: "hidden" }}
				>
					<div style={{ height: "100%", overflow: "auto", padding: 8 }}>
						<FilesTab patient={patient} setErrorMessages={setErrorMessages} />
					</div>
				</TabPane>

				{/* Forms */}
				<TabPane
					tab={
						<span style={{ fontSize: 12 }}>
							<FormOutlined /> Forms
						</span>
					}
					key="forms"
					style={{ height: "100%", overflow: "hidden" }}
				>
					<div style={{ height: "100%", overflow: "auto", padding: 8 }}>
						<FormsTab updatedPatient={patient} setUpdatedPatient={setPatient} handleAutoSave={handleAutoSave} />
					</div>
				</TabPane>

				{/* Notifications */}
				<TabPane
					tab={
						<span style={{ fontSize: 12 }}>
							<BellOutlined /> Communication
						</span>
					}
					key="notifications"
					style={{ height: "100%", overflow: "hidden" }}
				>
					<div style={{ height: "100%", overflow: "auto", padding: 8 }}>
						<NotificationsTab recipient={patient} />
					</div>
				</TabPane>

				{/* Appointments */}
				<TabPane
					tab={
						<span style={{ fontSize: 12 }}>
							<CalendarOutlined /> Appointments
						</span>
					}
					key="appointments"
					style={{ height: "100%", overflow: "hidden" }}
				>
					<div style={{ height: "100%", overflow: "auto", padding: 8 }}>
						<AppointmentTab patient={patient} />
					</div>
				</TabPane>

				{/* Medical history */}
				<TabPane
					tab={
						<span style={{ fontSize: 12 }}>
							<HistoryOutlined /> History
						</span>
					}
					key="history"
					style={{ height: "100%", overflow: "hidden" }}
				>
					<div style={{ height: "100%", overflow: "auto", padding: 8 }}>
						<MedicalHistoryTab patient={patient} handleAutoSave={handleAutoSave} />
					</div>
				</TabPane>

				{/* Medications */}
				<TabPane
					tab={
						<span style={{ fontSize: 12 }}>
							<MedicineBoxOutlined /> Medications
						</span>
					}
					key="medications"
					style={{ height: "100%", overflow: "hidden" }}
				>
					<div style={{ height: "100%", overflow: "auto", padding: 8 }}>
						<MedicationsTab patient={patient} handleAutoSave={handleAutoSave} />
					</div>
				</TabPane>

				{/* Testing */}
				<TabPane
					tab={
						<span style={{ fontSize: 12 }}>
							<ExperimentOutlined /> Testing
						</span>
					}
					key="testing"
					style={{ height: "100%", overflow: "hidden" }}
				>
					<div style={{ height: "100%", overflow: "auto", padding: 8 }}>
						<TestingTab patient={patient} />
					</div>
				</TabPane>

				{/* Supplements */}
				<TabPane
					tab={
						<span style={{ fontSize: 12 }}>
							<ThunderboltOutlined /> Supplements
						</span>
					}
					key="supplements"
					style={{ height: "100%", overflow: "hidden" }}
				>
					<div style={{ height: "100%", overflow: "auto", padding: 8 }}>
						<SupplementsTab patient={patient} company={company} />
					</div>
				</TabPane>
			</Tabs>
		</>
	);
};

export default ConsultContentTabs;
